import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { Id } from 'types/definitions'
import { collection } from 'infra/collection'
import { Model } from 'infra/model'
import { observable, computed, action, makeObservable } from 'mobx'
import { task, TaskGroup } from 'mobx-task'
import { DocumentPopulate } from 'documents/dtos/document'
import { fromStream } from 'utils/mobx-util'
import { v4 } from 'uuid'
import {
  switchMap,
  distinctUntilChanged,
  map,
  find,
  takeUntil,
  filter,
  withLatestFrom,
  share,
} from 'rxjs/operators'
import { Subject, of, defer, Observable } from 'rxjs'
import Document from 'documents/Document'
import { withCancellation, shareBehavior, unpair } from 'utils/rx-util'
import { CancellationToken } from 'infra/cancellation'
import { DocumentPickerViewModel } from '../../views/DocumentPicker/DocumentPickerViewModel'

/**
 * Document requests view model, for requesting documents.
 * Used in the Documents question, but I foresee other uses in the future,
 * such as the ability for collaborators to request documents from others
 * in the chat. That would be dope.
 */
export default class DocumentRequestsViewModel {
  /**
   * Used to push aggregate changes from instances.
   */
  private changesSubject = new Subject()

  /**
   * Document request view models.
   */
  private documentRequestViewModels = collection({
    create: (descriptor: Partial<RequestedDocumentDescriptor>) => {
      const instance = new RequestedDocumentViewModel(this)
      instance.set(descriptor, { parse: true })
      // Emits a value when the item is removed from the collection.
      // Used to unsubscribe.
      const itemRemoved$ = this.documentRequestViewModels.removals$.pipe(
        find((i) => i === instance),
      )
      itemRemoved$.subscribe(() => instance.dispose())
      instance.changes$
        .pipe(takeUntil(itemRemoved$))
        .subscribe(this.changesSubject.next.bind(this.changesSubject))
      return instance
    },
  })

  /**
   * Uploads a supplemental document not tied to a particular predefined option.
   */
  @action.bound
  uploadSupplemental(file: File) {
    const requestedDoc = this.documentRequestViewModels.set({
      id: 'SUPPLEMENTAL_DOC_' + v4().replaceAll(/-/g, ''),
      title: file.name,
      supplemental: true,
    })
    requestedDoc.newDocument(file)
  }

  /**
   * Emits a value whenever there is a change to the selection.
   */
  changes$ = this.changesSubject.pipe(
    map(() => this.documentRequestDescriptors),
  )

  /**
   * Constructor.
   *
   * @param adapter
   */
  constructor(public adapter: DocumentRequestsViewModelPort) {
    makeObservable(this)
  }

  /**
   * Document requests.
   */
  @computed.struct
  get documentRequests() {
    return this.documentRequestViewModels.slice()
  }

  /**
   * Gets the document request descriptors with the selected document.
   */
  @computed.struct
  get documentRequestDescriptors() {
    return this.documentRequestViewModels.map((r) => r.toDescriptor())
  }

  /**
   * Reconciles the document requests.
   */
  reconcile(descriptors: RequestedDocumentDescriptor[]) {
    this.documentRequestViewModels.reset(descriptors)
  }
}

/**
 * Called by the `uploadNewDocument` in the adapter.
 * A Document's creation happens before it gets uploaded. Upload
 * progress is tracked on the document itself, so we need to deliver the document
 * to the underlying view model when it becomes available so we can show the
 * upload progress in the UI.
 */
export type DocumentCreatedDelegate = (document: Document) => void

export interface DocumentRequestsViewModelPort {
  /**
   * Scroll provider used for the Link Document feature.
   */
  scrollProvider(): InfiniteScrollProvider<Document>

  /**
   * Reactive getter for the documents. Should have the same filtering and sorting applied
   * as the scroll provider being used. This is what makes sure that whenever new docs come in
   * through events or are uploaded on the client that they show up without having to clear the scroll
   * provider and re-fetch.
   */
  documents(): Document[]

  /**
   * Delegate for fetching a document by ID.
   *
   * @param id
   * @param populate
   */
  fetchDocument(id: Id, populate: DocumentPopulate): Promise<Document | null>

  /**
   * Reactive delegate used to filter out documents that are not applicable for linking.
   * Can be reactive, as it is used in a computed.
   *
   * @param doc
   */
  canLinkDocument(doc: Document): boolean

  /**
   * Used to link the document.
   *
   * @param doc
   */
  linkDocument(doc: Document): Promise<unknown>

  /**
   * Used to download a document
   * @param doc
   */
  downloadDocument(doc: Document, inline?: boolean): void

  /**
   * Delegate for uploading a document.
   * @param descriptor the descriptor for the requested document
   * @param file the DOM file object to upload
   * @param onDocumentCreated delegate for providing the document when it is available
   * @param cancellationToken used to cancel the operation
   */
  uploadNewDocument(
    descriptor: RequestedDocumentDescriptor,
    file: File,
    onDocumentCreated: DocumentCreatedDelegate,
    cancellationToken: CancellationToken,
  ): Promise<unknown>
}

/**
 * Describes a requested document.
 */
export interface RequestedDocumentDescriptor {
  /**
   * ID that uniquely identifies the descriptor in the list.
   */
  readonly id: string
  /**
   * The title of the document request (for example, Form W-2).
   */
  readonly title: string
  /**
   * Tag IDs to assign to new documents being uploaded through this descriptor.
   * Optional.
   */
  readonly documentTagIds?: string[]
  /**
   * The document ID if one is already selected.
   */
  readonly documentId?: Id | null
  /**
   * If the document is not tied to a particular answer
   */
  readonly supplemental: boolean
}

/**
 * View model for a requested document.
 */
export class RequestedDocumentViewModel extends Model<
  RequestedDocumentDescriptor,
  RequestedDocumentViewModel
> {
  /**
   * The request ID.
   */
  id = 'EMPTY_THATS_PROBABLY_A_BUG'

  /**
   * Title to display to the user and use as the title for
   * any newly uploaded document.
   */
  @observable title = 'Document'

  /**
   * IDs of document tags to assign to new docs.
   */
  @observable.shallow documentTagIds: string[] = []

  /**
   * Whether this is a supplemental document and not tied to any input option.
   */
  @observable supplemental = false

  /**
   * Used by the view to render loaders, errors and what not.
   */
  tasks: TaskGroup<any, any>

  /**
   * Adapter for docs functionality.
   *
   * @private
   */
  private adapter: DocumentRequestsViewModelPort

  /**
   * Action inputs for the processing stream.
   */
  private documentInput$ = new Subject<DocumentInput>()

  /**
   * Deduplicates the input so that we don't attempt to fetch the doc that is currently selected
   * in case we reconcile the same ID.
   */
  private dedupedInput$: Observable<DocumentInput> = this.documentInput$.pipe(
    // To do this, we need the currently selected document.
    withLatestFrom(defer(() => this.document$)),
    filter(([nextInput, currentDoc]) => {
      // If there's not a current document, then
      // it would not be considered a change
      if (!currentDoc) {
        return nextInput.type !== 'ClearDocument'
      }

      // Any upload would be a change.
      if (nextInput.type === 'UploadNewDocument') {
        return true
      }

      // Otherwise, if the ID of whatever the input represents
      // is the same as the current document, ignore.
      const nextInputKey = getInputKey(nextInput)
      return nextInputKey !== currentDoc.id
    }),
    unpair(),
    // Ignores consecutive inputs that have the same ID.
    distinctUntilChanged(areInputsEqual),
    share(),
  )

  /**
   * Observable that manages the ((up)loaded) document.
   *
   * Since the consumer of this module will be providing the document
   * request descriptors (and their current values) reactively, declaratively and
   * with no regard for human life, we need to be able to reconcile our state safely.
   */
  private documentIO$ = this.dedupedInput$.pipe(
    // Ensures we don't have race conditions between
    // setting IDs -> async fetch, uploading, and setting the doc directly.
    // We return the pair of I/O so we can ignore sets that were caused by reconciliation.
    switchMap((input): Observable<[DocumentInput, Document | null]> => {
      switch (input.type) {
        case 'ClearDocument':
          return of([input, null])
        case 'SetDocument':
          return of([input, input.document])
        case 'SetID':
          return withCancellation((ct) =>
            this.fetch(input.documentId, ct),
          ).pipe(map((d) => [input, d || /* istanbul ignore next */ null]))
        case 'UploadNewDocument':
          return withCancellation((ct) => {
            // By using an Rx subject, we can emit the document when we have it
            // but wait with completing until upload is done.
            // God bless this lib.
            const subject = new Subject<Document | null>()
            const onDocumentCreated = subject.next.bind(subject)
            this.upload(input.file, onDocumentCreated, ct)
              // Errors are displayed in the UI using the task group,
              // so we can ignore it here.
              // We just want to reset the document and complete the subject.
              .catch((_) => subject.next(null))
              // When we are done, complete the subject as no more
              // updates will happen.
              .finally(subject.complete.bind(subject))
            return subject.asObservable()
          }).pipe(map((d) => [input, d]))
      }
    }),
    share(),
  )

  /**
   * Observable that keeps the current document, `null` if none is selected.
   */
  document$ = this.documentIO$.pipe(
    map(([, doc]) => doc),
    // Only keep a single subscriber, emit `null` as the initial value.
    shareBehavior<Document | null>(null),
  )

  /**
   * The document ID. Even if we couldn't fetch the document, the ID is still
   * there, so we can use that to identify a provided document.
   */
  documentId$ = this.documentIO$.pipe(
    map(([input, doc]) => {
      if (doc) {
        return doc.id
      }

      switch (input.type) {
        case 'ClearDocument':
          return null
        case 'SetID':
          return input.documentId
        default:
          return null
      }
    }),
    shareBehavior<Id | null>(null),
  )

  /**
   * Observable that only emits the document when it was changed
   * outside reconciliation.
   */
  changes$ = this.documentIO$.pipe(
    // Don't emit the fetched doc if we were reconciling.
    filter(([input]) => input.type !== 'SetID'),
    map(([, doc]) => doc),
    share(),
  )

  /**
   * MobX<>Rx maps
   */
  private $ = {
    document: fromStream(null, this.document$),
    documentId: fromStream(Id.none, this.documentId$),
  }

  /**
   * Document picker view model.
   */
  documentPickerViewModel: DocumentPickerViewModel

  /**
   * Constructor.
   */
  constructor(private parent: DocumentRequestsViewModel) {
    super()
    makeObservable(this)
    this.adapter = parent.adapter
    this.clear = this.clear.bind(this)
    this.setDocument = this.setDocument.bind(this)
    this.newDocument = this.newDocument.bind(this)
    this.download = this.download.bind(this)
    this.tasks = TaskGroup<any, any>([this.fetch, this.upload])

    this.documentPickerViewModel = new DocumentPickerViewModel({
      scrollProvider: this.adapter.scrollProvider.bind(this.adapter),
      documents: this.adapter.documents.bind(this.adapter),
      onSelect: async (doc: Document) => {
        this.setDocument(doc)
        await this.adapter.linkDocument(doc)
        this.documentPickerViewModel.close()
      },
      isSelected: (doc: Document): boolean => {
        return this.document === doc
      },
      canLinkDocument: (doc: Document): boolean => {
        // This is used to filter out documents we already selected, but if it was selected
        // by this VM then we still want to show it in the list.
        if (this.document === doc) {
          return true
        }
        return this.adapter.canLinkDocument(doc)
      },
    })
  }

  /**
   * The currently selected document.
   */
  @computed
  get document(): Document | null {
    return this.$.document()
  }

  /**
   * ID of the currently selected document. If for some reason the fetch failed,
   * this will still be set while `document` will be null.
   */
  @computed
  get documentId(): Id | null {
    return this.$.documentId()
  }

  /**
   * Sets the document.
   * @param document
   */
  setDocument(document: Document) {
    this.documentInput$.next({ document, type: 'SetDocument' })
  }

  /**
   * Clears the selected document if one is selected.
   */
  clear() {
    this.documentInput$.next({ type: 'ClearDocument' })
  }

  /**
   * Starts uploading a document.
   *
   * @param file
   */
  newDocument(file: File) {
    this.documentInput$.next({
      file,
      type: 'UploadNewDocument',
    })
  }

  /**
   * Creates a descriptor for this view model.
   */
  toDescriptor(): RequestedDocumentDescriptor {
    return {
      id: this.id,
      title: this.title,
      documentId: this.documentId,
      documentTagIds: this.documentTagIds.slice(),
      supplemental: this.supplemental,
    }
  }

  /**
   * Parse the attributes.
   *
   * @param attrs
   */
  parse(
    attrs: Partial<RequestedDocumentDescriptor>,
  ): Partial<RequestedDocumentViewModel> {
    this.documentInput$.next(
      attrs.documentId
        ? { type: 'SetID', documentId: attrs.documentId }
        : { type: 'ClearDocument' },
    )
    return {
      id: attrs.id,
      title: attrs.title,
      supplemental: attrs.supplemental,
      documentTagIds: attrs.documentTagIds || this.documentTagIds,
    }
  }

  download() {
    if (this.document) {
      this.adapter.downloadDocument(this.document)
    }
  }

  /**
   * Disposes the underlying observables.
   */
  dispose() {
    this.documentInput$.complete()
  }

  /**
   * Called when we receive a new document ID.
   */
  private fetch = task.resolved(
    async (
      id: string,
      ct: CancellationToken,
    ): Promise<Document | null | undefined> =>
      ct.race(this.adapter.fetchDocument(id, { history: true, tags: true })),
  )

  /**
   * Uploads the document.
   */
  private upload = task.resolved(
    async (
      file: File,
      onDocumentCreated: DocumentCreatedDelegate,
      ct: CancellationToken,
    ) => {
      await Promise.race([
        this.adapter.uploadNewDocument(
          this.toDescriptor(),
          file,
          onDocumentCreated,
          ct,
        ),
        ct.cancellation,
      ])
    },
  )
}

/**
 * Ways to provide a document.
 */
type DocumentInput = SetID | ClearDocument | SetDocument | UploadNewDocument

/**
 * Sets the document ID, will result in a fetch for the doc.
 * Used when reconciling. DO NOT USE OUTSIDE RECONCILING as it will
 * not emit a change.
 */
interface SetID {
  type: 'SetID'
  documentId: string
}

/**
 * Clears the currently selected document.
 */
interface ClearDocument {
  type: 'ClearDocument'
}

/**
 * Sets the document directly, no fetch needed.
 */
interface SetDocument {
  type: 'SetDocument'
  document: Document
}

/**
 * Starts uploading a new document
 */
interface UploadNewDocument {
  type: 'UploadNewDocument'
  file: File
}

/**
 * Whether inputs are equal, used for ignoring consecutive changes of the same input
 * if relevant.
 *
 * @param left
 * @param right
 */
function areInputsEqual(left: DocumentInput, right: DocumentInput) {
  // When either side is an "upload", we don't dedupe.
  if (left.type === 'UploadNewDocument' || right.type === 'UploadNewDocument') {
    return false
  }

  return getInputKey(left) === getInputKey(right)
}

/**
 * Normalizes input key.
 *
 * @param input
 */
function getInputKey(input: DocumentInput) {
  switch (input.type) {
    case 'ClearDocument':
      return null
    case 'SetDocument':
      return Id(input.document.id)
    case 'SetID':
      return Id(input.documentId)
  }
}
